class NewsletterPopup extends HTMLElement {
    constructor() {
        super();

        const configuredDelay = Number.parseInt(this.getAttribute('data-delay'), 10) || 12000;
        this.timeToShow = Math.max(configuredDelay, 8000);
        this.expiresDate = Number.parseInt(this.getAttribute('data-expire'), 10) || 7;
        this.autoOpenEligible = this.getCookie('newsletter-popup') === '' && !this.isSubscribed();
        this.previousFocus = null;
        this.openTimer = null;
        this.imageTimer = null;
        this.autoOpenOnScroll = document.body.classList.contains('template-index');
        this.scrollIntent = false;
        this.blockingBodyClasses = [
            'cart-sidebar-show',
            'cart-sidebar-open',
            'menu-open',
            'menu_open',
            'sidebar-menu-open',
            'quickshop-popup-show',
            'quick_shop_popup_mobile',
            'quick-view-show',
            'quick-shop-show',
            'edit-cart-show',
            'add-to-cart-show',
            'cart-modal-show',
            'cart-upsell-show'
        ];

        this.onBodyClickEvent = this.onBodyClickEvent.bind(this);
        this.onKeydownEvent = this.onKeydownEvent.bind(this);
        this.onBodyClassChange = this.onBodyClassChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.onScrollIntent = this.onScrollIntent.bind(this);
        this.onPageScroll = this.onPageScroll.bind(this);

        document.body.addEventListener('click', this.onBodyClickEvent);
        document.addEventListener('keydown', this.onKeydownEvent);

        this.querySelectorAll('[data-close-newsletter-popup]').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();
                this.close(true);
            });
        });

        this.form = this.querySelector('#ContactPopup');
        this.sellfluxPending = false;
        this.sellfluxAccepted = '';
        this.resumeNativeSubmit = false;
        this.phoneInput = this.querySelector('#NewsletterFormPhone--Popup');
        this.phoneInput?.addEventListener('input', () => this.phoneInput.setCustomValidity(''));

        if (this.form) {
            this.onFormSubmit = (event) => {
                if (event.target === this.form) this.handleSubmit(event);
            };
            window.addEventListener('submit', this.onFormSubmit, true);
            this.form.addEventListener('focusin', (event) => {
                if (window.innerWidth <= 767 && event.target.matches('input')) {
                    window.setTimeout(() => {
                        event.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    }, 280);
                }
            });
        }

        this.bodyClassObserver = new MutationObserver(this.onBodyClassChange);
        this.bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        if (this.autoOpenEligible) {
            if (this.autoOpenOnScroll) {
                window.addEventListener('wheel', this.onScrollIntent, { passive: true });
                window.addEventListener('touchmove', this.onScrollIntent, { passive: true });
                window.addEventListener('scroll', this.onPageScroll, { passive: true });
            } else {
                this.scheduleOpen(this.timeToShow);
            }
        }
    }

    disconnectedCallback() {
        document.body.removeEventListener('click', this.onBodyClickEvent);
        document.removeEventListener('keydown', this.onKeydownEvent);
        this.bodyClassObserver?.disconnect();
        window.removeEventListener('submit', this.onFormSubmit, true);
        window.clearTimeout(this.openTimer);
        window.clearTimeout(this.imageTimer);
        window.removeEventListener('wheel', this.onScrollIntent);
        window.removeEventListener('touchmove', this.onScrollIntent);
        window.removeEventListener('scroll', this.onPageScroll);
    }

    onScrollIntent(event) {
        if (event.isTrusted) this.scrollIntent = true;
    }

    onPageScroll() {
        if (!this.autoOpenEligible || this.isOpen() || !this.scrollIntent) return;

        window.clearTimeout(this.openTimer);
        // Wait for the scrolling gesture to settle before presenting the offer.
        if (window.scrollY >= Math.min(240, window.innerHeight * 0.25)) {
            this.scheduleOpen(350);
        }
    }

    scheduleOpen(delay = 1500) {
        window.clearTimeout(this.openTimer);
        this.openTimer = window.setTimeout(() => {
            if (!this.autoOpenEligible || this.isOpen()) return;

            if (document.visibilityState === 'visible' && !this.hasBlockingInterface()) {
                this.open();
            } else {
                this.scheduleOpen(1500);
            }
        }, delay);
    }

    open() {
        if (this.hasBlockingInterface() || this.isOpen()) return;

        this.previousFocus = document.activeElement;
        document.body.classList.add('newsletter-show');
        this.setAttribute('aria-hidden', 'false');

        window.clearTimeout(this.imageTimer);
        this.imageTimer = window.setTimeout(() => {
            if (this.isOpen()) document.body.classList.add('show-newsletter-image');
        }, 350);

        window.setTimeout(() => {
            const title = this.querySelector('#NewsletterPopupTitle');
            const closeButton = this.querySelector('.halo-popup-close');
            (title || closeButton)?.focus();
        }, 80);
    }

    close(rememberDismissal = true) {
        if (rememberDismissal) {
            this.setCookie('newsletter-popup', 'dismissed', this.expiresDate);
            this.autoOpenEligible = false;
        }

        document.body.classList.remove('newsletter-show', 'show-newsletter-image');
        this.setAttribute('aria-hidden', 'true');
        window.clearTimeout(this.openTimer);
        window.clearTimeout(this.imageTimer);

        if (this.previousFocus instanceof HTMLElement && document.contains(this.previousFocus)) {
            this.previousFocus.focus();
        }
    }

    async handleSubmit(event) {
        if (event.defaultPrevented) return;

        if (this.resumeNativeSubmit || !this.getAttribute('data-sellflux-url')) {
            this.setCookie('newsletter-popup', 'submitted', this.expiresDate);
            this.autoOpenEligible = false;
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.sellfluxPending) return;

        const honeypot = this.form.querySelector('#NewsletterWebsite--Popup');
        if (honeypot?.value.trim()) {
            this.form.reset();
            this.close(true);
            return;
        }

        const nameInput = this.form.querySelector('[name="contact[first_name]"]');
        const emailInput = this.form.querySelector('[name="contact[email]"]');
        nameInput.value = this.normalizeContactName(nameInput.value);
        emailInput.value = emailInput.value.trim().toLowerCase();
        const name = nameInput.value;
        const phone = this.normalizeBrazilianPhone(this.phoneInput.value);
        this.phoneInput.setCustomValidity(phone ? '' : this.getAttribute('data-phone-error'));
        if (!this.form.reportValidity()) return;

        const payload = new URLSearchParams({
            name,
            email: emailInput.value,
            phone,
        });
        const originQuery = Object.fromEntries(new URLSearchParams(window.location.search));
        if (Object.keys(originQuery).length) payload.set('origin_query', JSON.stringify(originQuery));

        const fingerprint = payload.toString();
        const status = this.querySelector('[data-sellflux-status]');
        const submitter = event.submitter;
        const button = this.form.querySelector('[type="submit"]');
        const wasDisabled = button?.disabled || false;
        const fields = [nameInput, emailInput, this.phoneInput];
        const readOnlyStates = fields.map((field) => field.readOnly);
        const restoreFields = () => fields.forEach((field, index) => { field.readOnly = readOnlyStates[index]; });
        fields.forEach((field) => { field.readOnly = true; });
        this.sellfluxPending = true;
        this.form.setAttribute('aria-busy', 'true');
        if (button) button.disabled = true;
        if (status) {
            status.hidden = true;
            status.textContent = '';
        }

        try {
            if (this.sellfluxAccepted !== fingerprint) {
                const controller = new AbortController();
                const timeout = window.setTimeout(() => controller.abort(), 12000);
                try {
                    const response = await fetch(this.getAttribute('data-sellflux-url'), {
                        method: 'POST',
                        mode: 'cors',
                        credentials: 'omit',
                        redirect: 'error',
                        body: payload,
                        signal: controller.signal,
                    });
                    if (!response.ok) throw new Error('Sellflux request rejected');
                    this.sellfluxAccepted = fingerprint;
                } finally {
                    window.clearTimeout(timeout);
                }
            }

            restoreFields();
            this.phoneInput.value = phone;
            this.resumeNativeSubmit = true;
            if (button) button.disabled = wasDisabled;
            try {
                if (submitter?.form === this.form && !submitter.disabled) {
                    this.form.requestSubmit(submitter);
                } else {
                    this.form.requestSubmit();
                }
            } finally {
                this.resumeNativeSubmit = false;
            }
        } catch (error) {
            if (status) {
                status.textContent = this.getAttribute('data-sellflux-error');
                status.hidden = false;
            }
        } finally {
            restoreFields();
            this.sellfluxPending = false;
            this.form.removeAttribute('aria-busy');
            if (button) button.disabled = wasDisabled;
        }
    }

    normalizeContactName(value) {
        const particles = new Set(['da', 'das', 'de', 'do', 'dos', 'e']);
        return value.normalize('NFC').trim().replace(/\s+/gu, ' ').split(' ').map((word, index) => {
            const lower = word.toLocaleLowerCase('pt-BR');
            if (index > 0 && particles.has(lower)) return lower;
            return lower.replace(/(^|[-'’])(\p{L})/gu, (_, separator, letter) =>
                separator + letter.toLocaleUpperCase('pt-BR'));
        }).join(' ');
    }

    normalizeBrazilianPhone(value) {
        const digits = value.replace(/\D/g, '');
        const national = digits.length === 13 && digits.startsWith('55') ? digits.slice(2) : digits;
        const areaCodes = [
            '11', '12', '13', '14', '15', '16', '17', '18', '19',
            '21', '22', '24', '27', '28',
            '31', '32', '33', '34', '35', '37', '38',
            '41', '42', '43', '44', '45', '46', '47', '48', '49',
            '51', '53', '54', '55', '61', '62', '63', '64', '65',
            '66', '67', '68', '69', '71', '73', '74', '75', '77',
            '79', '81', '82', '83', '84', '85', '86', '87', '88',
            '89', '91', '92', '93', '94', '95', '96', '97', '98', '99',
        ];
        if (national.length !== 11 || national[2] !== '9' || !areaCodes.includes(national.slice(0, 2))) return '';
        if (value.trim().startsWith('+') && !(digits.length === 13 && digits.startsWith('55'))) return '';
        return `+55${national}`;
    }

    markSubscribed() {
        this.setCookie('newsletter-popup', 'subscribed', 365);
        this.autoOpenEligible = false;
        window.clearTimeout(this.openTimer);

        try {
            window.localStorage.setItem('dm-newsletter-subscribed', 'true');
        } catch (error) {
            // Cookies remain the fallback when storage is unavailable.
        }
    }

    isSubscribed() {
        if (this.getCookie('newsletter-popup') === 'subscribed') return true;

        try {
            return window.localStorage.getItem('dm-newsletter-subscribed') === 'true';
        } catch (error) {
            return false;
        }
    }

    hasBlockingInterface() {
        return this.blockingBodyClasses.some((className) => document.body.classList.contains(className));
    }

    isOpen() {
        return document.body.classList.contains('newsletter-show');
    }

    onBodyClassChange() {
        if (!this.isOpen() || !this.hasBlockingInterface()) return;

        this.close(false);

        if (this.autoOpenEligible) {
            this.scheduleOpen(2000);
        }
    }

    onBodyClickEvent(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        if (target.closest('[data-open-newsletter-popup]')) {
            if (!this.hasBlockingInterface()) this.open();
            return;
        }

        if (this.isOpen() && !this.contains(target)) {
            this.close(true);
        }
    }

    onKeydownEvent(event) {
        if (this.autoOpenOnScroll && event.isTrusted && ['ArrowDown', 'PageDown', 'End', ' '].includes(event.key)) {
            this.scrollIntent = true;
        }
        if (!this.isOpen()) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            this.close(true);
            return;
        }

        if (event.key !== 'Tab') return;

        const focusableElements = this.getFocusableElements();
        const fallback = this.querySelector('.halo-popup-close') || this.querySelector('#NewsletterPopupTitle');

        if (focusableElements.length === 0) {
            event.preventDefault();
            fallback?.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeIndex = focusableElements.indexOf(document.activeElement);

        if (activeIndex === -1) {
            event.preventDefault();
            (event.shiftKey ? lastElement : firstElement).focus();
        } else if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    }

    getFocusableElements() {
        const selector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(',');

        return Array.from(this.querySelectorAll(selector)).filter((element) => {
            if (!(element instanceof HTMLElement)) return false;
            if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

            const style = window.getComputedStyle(element);
            return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
        });
    }

    setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const secure = window.location.protocol === 'https:' ? ';Secure' : '';
        document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;SameSite=Lax${secure}`;
    }

    getCookie(name) {
        const prefix = `${name}=`;
        const cookies = document.cookie.split(';');

        for (let index = 0; index < cookies.length; index += 1) {
            const cookie = cookies[index].trim();
            if (cookie.indexOf(prefix) === 0) {
                return decodeURIComponent(cookie.substring(prefix.length));
            }
        }

        return '';
    }
}

if (!customElements.get('newsletter-popup')) {
    customElements.define('newsletter-popup', NewsletterPopup);
}

class NewsletterMessagePopup extends HTMLElement {
    constructor() {
        super();

        this.closeButton = this.querySelector('[data-close-newsletter-message-popup]');
        this.onBodyClickEvent = this.onBodyClickEvent.bind(this);
        this.onKeydownEvent = this.onKeydownEvent.bind(this);
        this.onClickEvent = this.onClickEvent.bind(this);

        this.closeButton?.addEventListener('click', () => this.close());
        document.body.addEventListener('click', this.onBodyClickEvent);
        document.addEventListener('keydown', this.onKeydownEvent);
        this.addEventListener('click', this.onClickEvent);
    }

    disconnectedCallback() {
        document.body.removeEventListener('click', this.onBodyClickEvent);
        document.removeEventListener('keydown', this.onKeydownEvent);
        this.removeEventListener('click', this.onClickEvent);
    }

    close() {
        document.body.classList.remove('newsletter-message-show');
        this.setAttribute('aria-hidden', 'true');
    }

    onBodyClickEvent(event) {
        if (!document.body.classList.contains('newsletter-message-show')) return;
        if (event.target instanceof Element && !this.contains(event.target)) this.close();
    }

    onKeydownEvent(event) {
        if (event.key === 'Escape' && document.body.classList.contains('newsletter-message-show')) {
            event.preventDefault();
            this.close();
        }
    }

    onClickEvent(event) {
        const button = event.target instanceof Element ? event.target.closest('[data-copy-newsletter-coupon]') : null;
        if (!button) return;

        const coupon = button.getAttribute('data-coupon') || '';
        const status = this.querySelector('[data-newsletter-copy-status]');

        this.copyText(coupon).then(() => {
            if (status) status.textContent = 'Cupom copiado!';
            button.textContent = 'Copiado!';
            window.setTimeout(() => {
                button.textContent = 'Copiar cupom';
            }, 1800);
        });
    }

    copyText(value) {
        const fallbackCopy = () => new Promise((resolve) => {
            const textarea = document.createElement('textarea');
            textarea.value = value;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            textarea.remove();
            resolve();
        });

        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(value).catch(fallbackCopy);
        }

        return fallbackCopy();
    }
}

if (!customElements.get('newsletter-message-popup')) {
    customElements.define('newsletter-message-popup', NewsletterMessagePopup);
}
